import { forEachObjIndexed, init, is, pathOr, without } from 'ramda';
import { FieldJson } from 'edocatbase/Form';
import { FieldJsonExtension } from "../types/types";

const moment = window.moment;

const getAsyncSelectValue = (value: any, returnValue = '') => {
  if (!value) return returnValue;
  if (is(Object, value)) return value.value || value.nodeRef;

  return value || '';
};

// search form submit
const getSubmitObjectForSearch = (form: { [key: string]: FieldJson & FieldJsonExtension }, withLabels: boolean) => {
  const properties: { [key: string]: any } = {};
  let associations: any[] = [];
  const key = 'configName';

  forEachObjIndexed(field => {
    const template = field.control.template;
    if (!(field?.control?.params && field?.control?.params?.display === 'hidden')) {
      if (!field?.value) return;

      if (
        template.endsWith('association.ftl') ||
        template.endsWith('packageitems.ftl') ||
        template.endsWith('authority.ftl') ||
        field.name.startsWith('assoc_')
      ) {
        const newValuesArray = Array.isArray(field.value) ? field.value : [field.value];
        const newValues = newValuesArray.filter(Boolean).map((item: any) => (item.nodeRef ? item.nodeRef : item.value));
        associations = [...associations, ...newValues.map((item: any) => `${field[key]}_${item}`)];
      } else if (template.endsWith('selectone.ftl') || template.endsWith('priority.ftl')) {
        if (is(Object, field.value)) {
          properties[field[key]] = withLabels ? field.value : field.value.value;
        } else {
          properties[field[key]] = field.value;
        }
      } else if (template.endsWith('selectmany.ftl')) {
        properties[field[key]] = withLabels ? field.value : field.value?.map((item: any) => item.value);
      } else if (template.endsWith('databox.ftl')) {
        properties[field[key]] = withLabels ? field.value : getAsyncSelectValue(field.value, '');
      } else if (template.endsWith('asyncselect.ftl')) {
        if (field.repeating) {
          properties[field[key]] = withLabels
            ? field.value
            : field.value?.map((item: any) => (item.value ? item.value : item.nodeRef));
        } else {
          properties[field[key]] = withLabels ? field.value : getAsyncSelectValue(field.value);
        }
      } else if (template.endsWith('category.ftl')) {
        properties[field[key]] = field.value ? field.value.map((item: any) => item.value) : '';
      } else if (template.endsWith('noderef.ftl')) {
        if (withLabels) {
          properties[field[key]] = field.value;
        } else {
          properties[field[key]] = field.value && field.value.value ? field.value.value : field.value;
        }
      } else if (Array.isArray(field.value)) {
        properties[field[key]] = field.value;
      } else if (
        template.endsWith('email-notification.ftl') ||
        template.endsWith('checkbox.ftl')
      ) {
        if (field?.value !== "") {
          if(field?.value?.value !== undefined){
            properties[field[key]] = field?.value?.value === 'true';
          } else {
            properties[field[key]] = field?.value === 'true';
          }
        }
      } else if (template.endsWith('datediff.ftl')) {
        const value = field.value?.date;
        properties[field[key]] = value ? value.toISOString() : null;
      } else if (
        template.endsWith('daterange.ftl') ||
        template.endsWith('date.ftl') ||
        field.dataType === 'date'
      ) {
        if (field.value && (field.value.start || field.value.end)) {
          const start = field.value.start
            ? moment(field.value.start).toISOString()
            : moment('01.01.1970 02:00', 'DD.MM.YYYY HH:mm').toISOString();
          const end = field.value.end ? moment(field.value.end).toISOString() : moment().toISOString();
          properties[field[key]] = {
            minValue: start,
            maxValue: end,
          };
        }
      } else if (template.endsWith('number.ftl') || template.endsWith('double.ftl')) {
        if (field.value && (field.value.min || field.value.max)) {
          const min = field.value.min ? field.value.min : 'MIN';
          const max = field.value.max ? field.value.max : 'MAX';
          properties[field[key]] = {
            minValue: min,
            maxValue: max,
          };
        } else {
          properties[field[key]] = field.value;
        }
      } else if (!template.endsWith('hidden.ftl')) {
        properties[field[key]] = `${field.value || ''}`;
      }
    }
  }, form);

  let filteredProps: { [key: string]: any } = {};
  Object.keys(properties).forEach((key: string) => {
    if (properties[key]?.length === undefined || properties[key]?.length > 0) {
      filteredProps = {
        ...filteredProps,
        [key]: properties[key],
      }
    }
  })

  return {
    properties: filteredProps,
    ...associations.length > 0 && { associations }
  };
};

// global form submit
const prepareFormForSubmit = (
  form: { [key: string]: FieldJson & FieldJsonExtension },
  type?: string,
  withLabels = false,
  transitionSubmit = false,
  initialFields = [],
  ignoreAddedRemoved: boolean = false,
  tableField: boolean = false,
) => {
  const result = {} as { [key: string]: any };

  if (type === 'search') {
    return getSubmitObjectForSearch(form, withLabels);
  }

  // default form submit function
  forEachObjIndexed((field: FieldJson & FieldJsonExtension, index) => {
    if (!(field?.control?.params && field?.control?.params?.display === 'hidden')) {
      if (field.control.template.endsWith('number.ftl') || field.control.template.endsWith('double.ftl')) {
        result[index] = `${field.value}`;
      } else if(field.control.template.endsWith('packageitems.ftl') && field?.dataType == "packageItems") {
				const newValues = field.value
					? field.value.map((item: any) => (is(String, item) ? item : item.nodeRef || item.value))
					: [];
				result[`e${index}`] = newValues;
			} else if (
        field.control.template.endsWith('association.ftl') ||
				field.control.template.endsWith('packageitems.ftl') ||
        (field.repeating && field.control.template.endsWith('authority.ftl'))
      ) {
        if (window.location.href.includes('client-admin')) {
          const newValues = field.value
            ? field.value.map((item: any) => (is(String, item) ? item : item.nodeRef || item.value))
            : [];
          result[`e${index}`] = newValues;
        } else if (ignoreAddedRemoved) {
          result[index] = field.value
            ? field.value.map((item: any) => (is(String, item) ? item : item.nodeRef || item.value))
            : [];
        } else {
          // @ts-ignore
          // Get all assoc initial values and convert them into array
          const oldValuesInString = initialFields?.[index]?.value ?? '';
          const oldValuesToArray =
            oldValuesInString.length > 0
              ? oldValuesInString.split
                ? oldValuesInString.split(',')
                : oldValuesInString
              : [];
          // Get all actual assoc values and map them into array
          const newValues = field.value
            ? field.value.map((item: any) => (is(String, item) ? item : item.nodeRef || item.value))
            : [];
          // Compare initial and actual values and get the difference
          const added = without(oldValuesToArray, newValues).join(',');
          const removed = without(newValues, oldValuesToArray).join(',');

          result[`${index}_added`] = added.length > 0 ? added : newValues.join(',');
          result[`${index}_removed`] = removed;
        }
      } else if (field.control.template.endsWith('selectone.ftl') || field.control.template.endsWith('priority.ftl')) {
        if (is(Object, field.value)) {
          result[index] = field.value.value;
        } else {
          result[index] = field.value ?? '';
        }
      } else if (field.control.template.endsWith('/transitions.ftl')) {
        if (transitionSubmit) {
          result[index] = field.value ? field.value.split('|')[0] : '';
        }
      } else if (
        field.control.template.endsWith('activiti-transitions-iso.ftl') ||
        field.control.template.endsWith('activiti-transitions.ftl')
      ) {
        if (transitionSubmit) {
          result['prop_transitions'] = 'Next';
          result[index] = field.value;
        }
      } else if (field.control.template.endsWith('selectmany.ftl')) {
        result[index] = (typeof field?.value?.[0] === "object" && field?.value?.[0] !== null && field.value?.map((item: any) => item?.value).join(',')) || field?.value?.toString() || [];
      } else if (field.control.template.endsWith('filepicker.ftl')) {
        result[index] = field.value;
      } else if (field.control.params?.format === 'postalCode') {
        const value = field?.value?.toString() ?? '';
        result[index] = value.replace(' ', '');
      } else if (field.control.template.endsWith('databox.ftl')) {
        result[index] = getAsyncSelectValue(field.value, '');
      } else if (field.control.template.endsWith('asyncselect.ftl')) {
        if (field.control.params.association) {
          if (field.repeating) {
            const newValues = field.value?.map((item: any) => (item.value ? item.value : item.nodeRef)) || [];
            result[`e${index}`] = newValues.join(',');
          } else {
            result[`e${index}`] = getAsyncSelectValue(field.value);
          }
        } else if (field.repeating) {
          result[index] = field.value?.map((item: any) => (item.value ? item.value : item.nodeRef)).join(',') || [];
        } else {
          result[index] = getAsyncSelectValue(field.value);
        }
      } else if (!tableField && !field.repeating && field.control.template.endsWith('authority.ftl')) {
        result[`e${index}`] = is(String, field.value) ? field.value : field.value?.value || '';

      } else if (tableField && !field.repeating && field.control.template.endsWith('authority.ftl')) {
        result[index] = is(String, field.value) ? field.value : field.value?.value || '';
      } else if (field.control.template.endsWith('taskowner.ftl')) {
        result[index] = field.value ? field.value.value : '';
      } else if (field.control.template.endsWith('category.ftl')) {
        result[index] = field.value ? field.value.map((item: any) => item.value).join(',') : '';
      } else if (field.control.template.endsWith('noderef.ftl')) {
        result[index] = field.value && field.value.value ? field.value.value : field.value;
      } else if (field.control.template.endsWith('tableMM.ftl')) {
        result[index] = field.value && (Array.isArray(field.value) && field.value.length > 0) ?
          JSON.stringify(field.value) : '';
      } else if (field.control.template.endsWith('aresfield.ftl')) {
        result[index] = field.value ? JSON.stringify(field.value) : '';
      } else if (Array.isArray(field.value)) {
        result[index] = field.value.join(',');

        if (field.dataType !== 'category') {
          result[`${index}_isListProperty`] = true;
        }
      } else if (
        field.control.template.endsWith('checkbox.ftl') ||
        field.control.template.endsWith('email-notification.ftl')
      ) {
        if (field.control.params.sendAsBoolean) {
          result[index] = field.control.params.revertValue ? !Boolean(field.value) : Boolean(field.value);
        } else {
          result[index] = field.control.params.revertValue
            ? field.value
              ? 'false'
              : 'true'
            : String(field.value) === 'true'
              ? 'true'
              : 'false';
        }
      } else if (field.control.template.endsWith('datediff.ftl')) {
        const value = field.value?.date;
        result[index] = value ? value.toISOString() : '';
      } else if (field.control.template.endsWith('daterange.ftl')) {
        if (field.value && (field.value.start || field.value.end)) {
          const start = field.value.start
            ? moment(field.value.start).toISOString()
            : moment('01.01.1970 02:00', 'DD.MM.YYYY HH:mm').toISOString();
          const end = field.value.end ? moment(field.value.end).toISOString() : moment().toISOString();
          result[`${index}-date-range`] = [start, end];
        }
      } else if (field.control.template.endsWith('date.ftl') || field.dataType === 'date') {
        if (!field.value) {
          result[index] = '';
        } else {
          result[index] = field.dateFormat
            ? moment(field.value).format(field.dateFormat)
            : moment(field.value).toISOString() || '';
        }
      } else if (!field.control.template.endsWith('hidden.ftl') || !field.control.template.endsWith('content.ftl')) {
        result[index] = `${field.value || ''}`;
      }

      if (field.dataType === 'category') {
        result[`${index}_isCategory`] = true;
      }
    }
  }, form);
  return result;
};

export default prepareFormForSubmit;
